// "In pool" cell: LB membership and traffic share, edited in place.
//
// These two fields are the whole of what the Upstream pool page existed to
// change, so they ride along in the workers table as a column rather than as a
// second page over the same rows. Both dirty the nginx config, which is why the
// Render & Reload control sits on this page's command bar.

import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Switch } from "@veil/ui/components/switch";
import {
	Tooltip,
	TooltipContent,
	TooltipTrigger,
} from "@veil/ui/components/tooltip";
import { toast } from "sonner";
import { api, unwrap } from "@/lib/api";
import type { WorkerRow } from "@/lib/workers/types";
import { InlineNumberEdit } from "./inline-number-edit";

interface WorkerPatch {
	enabled?: boolean;
	weight?: number;
}

export function WorkerPoolCell({
	worker,
	canEdit,
}: {
	worker: WorkerRow;
	canEdit: boolean;
}) {
	const queryClient = useQueryClient();

	const patch = useMutation({
		mutationFn: (body: WorkerPatch) =>
			unwrap(api.api.workers({ hostname: worker.hostname }).patch(body)),
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: ["cluster"] });
			queryClient.invalidateQueries({ queryKey: ["workers"] });
		},
		onError: (err) =>
			toast.error(err instanceof Error ? err.message : "Update failed"),
	});

	if (!canEdit) {
		return (
			<span className="font-mono text-xs tabular-nums text-muted-foreground">
				{worker.enabled ? `yes · w=${worker.weight}` : "no"}
			</span>
		);
	}

	return (
		<div className="flex items-center gap-2">
			<Tooltip>
				<TooltipTrigger asChild>
					<Switch
						checked={worker.enabled}
						disabled={patch.isPending}
						onCheckedChange={(v) => patch.mutate({ enabled: v })}
						aria-label={`${worker.enabled ? "Remove" : "Add"} ${worker.hostname} ${worker.enabled ? "from" : "to"} the upstream pool`}
					/>
				</TooltipTrigger>
				<TooltipContent side="bottom" className="max-w-xs text-[11px]">
					Whether the load balancer routes traffic here. Turning it off drains
					the worker without stopping it — existing sessions keep running.
				</TooltipContent>
			</Tooltip>
			<Tooltip>
				<TooltipTrigger asChild>
					<span className="inline-flex">
						<InlineNumberEdit
							value={worker.weight}
							min={1}
							max={1000}
							onCommit={(v) => patch.mutate({ weight: v })}
						/>
					</span>
				</TooltipTrigger>
				<TooltipContent side="bottom" className="max-w-xs text-[11px]">
					Relative share of upstream traffic. A worker with weight 2 receives
					twice as many requests as one with weight 1.
				</TooltipContent>
			</Tooltip>
		</div>
	);
}
